// src/pages/builder/SingAlongPanel.js
//
// SING-ALONG STUDIO — Layout Builder panel.
//
// Turns a topic's song + lyrics into a sing-along: lyrics shown a section at a
// time with the current line lit as it is sung. Three ways in:
//
//   1. AUTO-SYNC  — src/scripts/align-lyrics.py listens to every song and
//                   writes first-pass timings. They land here marked
//                   "Auto — check it" and are NOT shown to students until
//                   someone opens them, checks them, and publishes.
//   2. TAP ALONG  — play the song, tap Space as each line starts. Or switch
//                   to TAP BY WORD and tap Space on every word: each line
//                   still gets one cue, plus the start of every word in it
//                   (cue.w), so the colour moves exactly with the singing.
//   3. FIX        — nudge, type or re-record any line.
//
// Then: publish to students (Music popup), and/or record an MP4.
//
// WHAT IT READS AND WRITES
// ------------------------
// Reads topicLibrary (for song + lyrics) and lyricSync. Writes lyricSync and,
// for a saved video, one file at videos/{slug}{year}singalong.mp4. It never writes
// topicLibrary or portalLayouts, so nothing here can change a saved page or a
// topic's existing video, song or lyrics.
//
// STYLING
// -------
// Builder classes (.lb-btn, --lb-*) for controls; panel rules live in
// components/singalong/singalong.css, scoped under `.lb-page .sa-panel`.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";
import {
  SUBJECTS,
  SUBJECT_LABELS,
  attachSingAlongVideo,
  attachTopicSong,
  exactKey,
  fileKeyOf,
  getAllLibraryTopics,
  legacyKey,
  makeToolKey,
} from "../../services/topicLibraryService";
import {
  FADE_SECONDS,
  MAX_LOGO_OPACITY,
  THEMES,
  THEME_ORDER,
  cleanCues,
  endAtOf,
  fadeLevelAt,
  fileFadesItself,
  formatTime,
  fullSongUrlOf,
  isFadedSong,
  getAllLyricSyncs,
  linesDiffer,
  mediaKey,
  nextSungLine,
  parseLyrics,
  parseTime,
  remapCues,
  saveLyricSync,
  stopTimeOf,
  syncMatchesSong,
  syncSignature,
  logoOpacityOf,
  themeKeyOf,
  toLyricLines,
  wordsOf,
} from "../../services/lyricSyncService";
import LyricsDisplay from "../../components/singalong/LyricsDisplay";
import {
  canExportVideo,
  exportSingAlongVideo,
  pickVideoMime,
} from "../../components/singalong/exportSingAlongVideo";
import ExportPreview from "../../components/singalong/ExportPreview";
import { canMakeFadedSong, makeFadedSong } from "../../components/singalong/makeFadedSong";
import "../../components/singalong/singalong.css";

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

const store = {
  get(key, fallback) {
    try {
      const v = window.localStorage.getItem(key);
      return v === null ? fallback : JSON.parse(v);
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* private window — fine */
    }
  },
};

let cueSeq = 0;
const withIds = (cues) => cues.map((c) => ({ ...c, id: (cueSeq += 1) }));
const sortCues = (cues) => [...cues].sort((a, b) => a.t - b.t);
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * motion -> motion2026singalong.mp4 (year = when the video is made).
 * Takes the TOOL KEY, so earth'swater saves earth_swater2026singalong.mp4 and
 * never overwrites earthswater2026singalong.mp4.
 */
const singAlongFileName = (slug, ext = "mp4") =>
  `${fileKeyOf(slug)}${new Date().getFullYear()}singalong.${String(ext).replace(/^\./, "")}`;

/**
 * motion -> motion2026song-faded.mp3, beside motion2026song.mp3 in music/.
 * "faded" keeps it out of match-media-to-topics.mjs, and the topic name at
 * the front means the Topic Library's "Find existing" (which starts its
 * search with the topic's slug) offers it straight away.
 */
const fadedSongFileName = (slug) => `${fileKeyOf(slug)}${new Date().getFullYear()}song-faded.mp3`;

const fileNameOf = (url) => {
  const key = mediaKey(url);
  return key.split("/").pop() || "";
};

const STATUS_TONES = {
  nosong: "grey",
  nolyrics: "grey",
  todo: "grey",
  stale: "red",
  auto: "amber",
  ready: "blue",
  live: "green",
};

export const statusOf = (topic, sync) => {
  if (!topic.musicUrl) return { key: "nosong", label: "No song file" };
  if (!parseLyrics(topic.lyrics).sung.length) return { key: "nolyrics", label: "No lyrics" };
  if (!sync || !(sync.cues || []).length) return { key: "todo", label: "Not synced" };
  if (!syncMatchesSong(sync, topic.musicUrl)) return { key: "stale", label: "Song changed" };
  if (sync.published) return { key: "live", label: "Live" };
  if (!sync.reviewed) return { key: "auto", label: "Auto — check it" };
  return { key: "ready", label: "Saved, not live" };
};

const videoStateOf = (sync) => {
  if (!sync?.videoUrl) return { key: "none", label: "—" };
  const sig = syncSignature({
    lines: sync.lines,
    cues: sync.cues,
    theme: sync.theme,
    title: sync.title,
    logoOpacity: sync.logoOpacity,
    endAt: sync.endAt,
  });
  return sig === sync.videoSig
    ? { key: "ok", label: "Video ✓" }
    : { key: "old", label: "Video out of date" };
};

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "todo", label: "Not synced" },
  { key: "check", label: "Needs a check" },
  { key: "live", label: "Live" },
];

const matchesFilter = (status, filter) => {
  if (filter === "all") return true;
  if (filter === "todo") return status.key === "todo";
  if (filter === "check") return ["auto", "stale", "ready"].includes(status.key);
  if (filter === "live") return status.key === "live";
  return true;
};

/* ------------------------------------------------------------------ */
/* Instructions                                                        */
/* ------------------------------------------------------------------ */

const HowItWorks = ({ open, onToggle }) => (
  <div className="sa-help">
    <button type="button" className="sa-help-toggle" onClick={onToggle}>
      <span>{open ? "▾" : "▸"}</span> How to make a sing-along
    </button>
    {open && (
      <ol className="sa-help-steps">
        <li>
          <strong>Pick a song below.</strong> A song needs a song file and lyrics in the
          Topic Library. Section names like <code>(Chorus)</code> become the labels on screen,
          and blank rows split the lyrics into pages.
        </li>
        <li>
          <strong>Fast way — auto-sync.</strong> Run the auto-sync script once on your
          computer (instructions are in <code>SINGALONG-SETUP.md</code>). It listens to every
          song and times the lines for you. Those songs show up here as{" "}
          <span className="sa-pill-inline tone-amber">Auto — check it</span>.
        </li>
        <li>
          <strong>Hand way — tap along.</strong> Open a song, press <kbd>● Record</kbd>, and
          tap <kbd>Space</kbd> the moment each line starts singing. Slow the song down to 0.75×
          if it helps — the timings still come out right.
          <br />
          <strong>More exact — tap by word.</strong> Switch the tap box to <kbd>By word</kbd> and
          tap <kbd>Space</kbd> on <em>every word</em> instead. The lines stay the same; each word
          just gets its own start, so the colour change lands right on the word. The big button
          shows which word is next and counts them.
        </li>
        <li>
          <strong>Check it.</strong> Press <kbd>▶ Play</kbd> and watch the preview. If a line
          lights up early or late, use <kbd>−0.1</kbd> / <kbd>+0.1</kbd> on that line, type the
          time, or re-record just that part.
        </li>
        <li>
          <strong>Set the ending (optional).</strong> Play to where the sing-along should finish
          and press <kbd>■ End sing-along here</kbd>. From that point the screen and the song
          fade out over 5 seconds, then stop — in the preview, for students, and in the video.
          Then <kbd>🎵 Make faded song</kbd> saves an MP3 that fades at the same spot, so the
          Music button ends with the sing-along too.
        </li>
        <li>
          <strong>Publish.</strong> Tick <em>Show this sing-along to students</em> and press{" "}
          <kbd>Save</kbd>. Students and teachers now get the sing-along (with a full-screen
          button for the smart board) when they open the song's Music button.
        </li>
        <li>
          <strong>Make a video (optional).</strong> After saving, press{" "}
          <kbd>🎬 Make video</kbd>. It records in real time — keep this tab open and visible
          until it finishes. Then download the MP4 or save it to SBL.
        </li>
        <li>
          <strong>Several at once.</strong> In the song list, tick the songs you want (or{" "}
          <kbd>Select all “Auto — check it” shown</kbd>) and press <kbd>🚀 Go live + make videos</kbd>.
          You see the list and how long the videos will take, and confirm. Each song is then saved as
          checked and shown to students, its video is recorded, saved to SBL storage and put on the
          topic’s 🎤 Sing-along button — one song after another. Keep the tab open and on screen until
          it finishes; <kbd>Stop after this song</kbd> ends the run cleanly. Untick{" "}
          <em>Also make each video</em> to only make them live.
        </li>
      </ol>
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/* Studio: one song                                                    */
/* ------------------------------------------------------------------ */

const DELAY_OPTIONS = [0, 0.1, 0.15, 0.2, 0.25, 0.3];
const RATE_OPTIONS = [0.5, 0.75, 1];
// Play pressed at (or after) the end marker starts this many seconds before it.
const ENDING_LEAD_IN = 5;

const Studio = ({ topic, sync, onClose, onSaved, onAttached, onSongAttached }) => {
  // The row already set topic.slug to the tool key (makeToolKey). Stripping
  // it again would put earth'swater's sing-along back on earthswater.
  const slug = topic.slug;
  const audioRef = useRef(null);
  const rafRef = useRef(0);
  const scriptRef = useRef(null);

  const initialLines = useMemo(
    () => (sync?.lines?.length ? toLyricLines(sync.lines) : toLyricLines(topic.lyrics)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [lines, setLines] = useState(initialLines);
  const parsed = useMemo(() => parseLyrics(lines), [lines]);
  const [cues, setCues] = useState(() =>
    withIds(cleanCues(sync?.cues, parseLyrics(initialLines)))
  );
  // The keyboard handler and stopRecording are long-lived callbacks; they
  // read the latest cues through this ref instead of a stale closure.
  const cuesRef = useRef(cues);
  cuesRef.current = cues;

  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [delay, setDelay] = useState(() => store.get("sbl.singalong.delay", 0.15));
  const [recording, setRecording] = useState(false);
  // "line" = one tap per line (the original way). "word" = one tap per word:
  // the line's cue is the first word, every later tap adds a word start to
  // cue.w. Remembered per browser.
  const [tapMode, setTapModeState] = useState(() => (store.get("sbl.singalong.tapMode", "line") === "word" ? "word" : "line"));
  const tapModeRef = useRef(tapMode);
  const setTapMode = (m) => {
    tapModeRef.current = m;
    setTapModeState(m);
    store.set("sbl.singalong.tapMode", m);
  };
  // Word mode only: which word of the pointer line the next tap is.
  const [wordPos, setWordPosState] = useState(0);
  const setWordPos = (n) => setWordPosState(n);
  const [pointer, setPointerState] = useState(parsed.sung[0] ?? -1);
  const pointerRef = useRef(parsed.sung[0] ?? -1);
  const pinnedRef = useRef(false);
  const sessionRef = useRef(null);

  const [themeKey, setThemeKey] = useState(() => themeKeyOf(sync?.theme));
  const [logoOpacity, setLogoOpacity] = useState(() => logoOpacityOf(sync));
  const [published, setPublished] = useState(Boolean(sync?.published));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { tone, text }
  const [showTimings, setShowTimings] = useState(() => (sync?.cues || []).length > 0);
  const [audioError, setAudioError] = useState("");
  const [follow, setFollow] = useState(() => store.get("sbl.singalong.follow", true));

  // "End sing-along here". From endAt the preview and the song fade out over
  // FADE_SECONDS, then playback stops — exactly like the video.
  //
  // Play honours the end every time (it used to only fade when playback had
  // been started BEFORE the marker, so pressing Play right after setting the
  // end played straight on with no fade). Pressing Play at or after the end
  // goes back ENDING_LEAD_IN seconds and plays into the fade.
  //
  // Free play (no fade): while recording taps, when "Play past the end" is
  // ticked (to pick a later ending by ear), or when ▶ on a line that comes
  // after the end is pressed.
  const [endAt, setEndAtState] = useState(() => endAtOf(sync));
  const endAtRef = useRef(endAt);
  const endingRef = useRef(false);
  const freeRef = useRef(false);
  const [playPast, setPlayPastState] = useState(false);
  const playPastRef = useRef(false);
  const setEndAt = (v) => {
    const next = Number.isFinite(v) && v > 0 ? round2(v) : null;
    endAtRef.current = next;
    setEndAtState(next);
    setDirty(true);
    // Set while the song is playing: the fade starts right there.
    armEnding();
  };
  const setPlayPast = (v) => {
    playPastRef.current = v;
    setPlayPastState(v);
    freeRef.current = v || Boolean(sessionRef.current);
    armEnding();
  };

  const setPointer = (i) => {
    pointerRef.current = i;
    setPointerState(i);
  };

  // The full-length recording. If the topic's Song is already the faded MP3,
  // work from the original so the end can still be moved later.
  const songUrl = fullSongUrlOf(sync, topic.musicUrl);

  const songChanged = Boolean(sync && (sync.cues || []).length && !syncMatchesSong(sync, topic.musicUrl));
  const lyricsChanged = linesDiffer(lines, topic.lyrics);

  const flash = (text, tone = "info") => setNotice({ text, tone, at: Date.now() });

  useEffect(() => {
    store.set("sbl.singalong.delay", delay);
  }, [delay]);

  useEffect(() => {
    store.set("sbl.singalong.follow", follow);
  }, [follow]);

  /* ---------- time loop ---------- */

  const tick = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    const end = endAtRef.current;
    if (endingRef.current && end !== null && !sessionRef.current) {
      if (a.currentTime >= stopTimeOf(end)) {
        a.pause();
        a.volume = 1;
        setTime(a.currentTime);
        return;
      }
      a.volume = fileFadesItself(a.duration, stopTimeOf(end)) ? 1 : fadeLevelAt(end, a.currentTime);
    } else if (a.volume !== 1) {
      a.volume = 1;
    }
    setTime(a.currentTime);
    if (!a.paused && !a.ended) rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  const play = async ({ free = false } = {}) => {
    const a = audioRef.current;
    if (!a) return;
    const end = endAtRef.current;
    freeRef.current = free || playPastRef.current || Boolean(sessionRef.current);
    // At or past the end: hear the last few seconds and the fade again.
    if (!freeRef.current && end !== null && a.currentTime >= end - 0.05) {
      a.currentTime = Math.max(0, end - ENDING_LEAD_IN);
      setTime(a.currentTime);
    }
    armEnding();
    try {
      await a.play();
    } catch (err) {
      setAudioError("The song would not play. Check the song file in the Topic Library.");
    }
  };
  const pause = () => audioRef.current?.pause();
  const togglePlay = () => (audioRef.current?.paused ? play() : pause());

  const seek = (t) => {
    const a = audioRef.current;
    if (!a) return;
    const max = Number.isFinite(a.duration) ? a.duration : t;
    a.currentTime = Math.max(0, Math.min(max, t));
    if (a.volume !== 1 && (endAtRef.current === null || a.currentTime < endAtRef.current)) a.volume = 1;
    setTime(a.currentTime);
  };

  // The ending fade is on whenever there is an end marker and this isn't
  // free play. tick() does the fading and the stop.
  const armEnding = () => {
    const a = audioRef.current;
    endingRef.current = endAtRef.current !== null && !freeRef.current;
    if (a && !endingRef.current) a.volume = 1;
  };

  const previewEnding = () => {
    if (endAt === null) return;
    seek(Math.max(0, endAt - 4));
    play();
  };

  /* ---------- recording ---------- */

  const lastCueBefore = (t) => {
    let found = null;
    cues.forEach((c) => {
      if (c.t <= t) found = c;
    });
    return found;
  };

  const startRecording = () => {
    const a = audioRef.current;
    if (!a) return;
    sessionRef.current = { from: a.currentTime, ids: new Set(), stack: [], building: null, unfinished: 0 };
    setWordPos(0);
    freeRef.current = true;
    armEnding();
    if (!pinnedRef.current) {
      const last = lastCueBefore(a.currentTime);
      setPointer(last ? nextSungLine(parsed, last.i) : parsed.sung[0] ?? -1);
    }
    pinnedRef.current = false;
    setRecording(true);
    setShowTimings(true);
    play();
  };

  const stopRecording = useCallback(() => {
    const a = audioRef.current;
    if (a) a.pause();
    const s = sessionRef.current;
    sessionRef.current = null;
    setRecording(false);
    setWordPos(0);
    if (!s || !s.ids.size) return;

    // Word mode: a line stopped part-way has only some of its word starts.
    // Partial word timings are useless (the player needs one per word), so
    // that line keeps its start and lights up by estimate instead.
    if (s.building) {
      s.unfinished += 1;
      unfinishedIdRef.current = s.building.id;
      s.building = null;
    }

    // Replace only the stretch that was tapped over: old timings between the
    // moment Record was pressed and the last tap. Anything after the last tap
    // is kept, so listening on past the part you fixed costs nothing.
    const stripId = unfinishedIdRef.current;
    unfinishedIdRef.current = null;
    const prev = cuesRef.current.map((c) =>
      c.id === stripId ? { ...c, w: undefined, e: undefined, wt: undefined } : c
    );
    const fresh = prev.filter((c) => s.ids.has(c.id));
    // In word mode a line's last word comes well after its start; count
    // the whole tapped stretch, not just the line starts.
    const lastTap = Math.max(...fresh.map((c) => c.t + (c.wt && c.w?.length ? c.w[c.w.length - 1] : 0)));
    const kept = prev.filter(
      (c) => s.ids.has(c.id) || c.t < s.from - 0.05 || c.t > lastTap + 0.3
    );
    const replaced = prev.length - kept.length;
    setCues(sortCues(kept));
    setNotice({
      tone: "ok",
      text:
        `Recorded ${fresh.length} line${fresh.length === 1 ? "" : "s"}` +
        (replaced > 0
          ? `; replaced ${replaced} older timing${replaced === 1 ? "" : "s"} in that stretch.`
          : ".") +
        (s.unfinished > 0
          ? ` ${s.unfinished} line${s.unfinished === 1 ? " was" : "s were"} stopped part-way, so ${
              s.unfinished === 1 ? "it keeps" : "they keep"
            } the line start but not the word times.`
          : "") +
        " Press ▶ Play to check it, then Save.",
      at: Date.now(),
    });
  }, []);

  // A line left half-tapped in word mode (because a different line was
  // clicked) loses its partial word times — see stopRecording.
  const unfinishedIdRef = useRef(null);
  const closeBuilding = (s) => {
    const b = s.building;
    if (!b) return;
    s.building = null;
    s.unfinished += 1;
    setCues((prev) => prev.map((c) => (c.id === b.id ? { ...c, w: undefined, e: undefined, wt: undefined } : c)));
  };

  /** Word mode: one tap = the next word of the pointer line. */
  const tapWord = (a, s, lineOverride) => {
    const t = round2(Math.max(0, a.currentTime - delay * a.playbackRate));
    if (lineOverride !== undefined && lineOverride !== null) {
      closeBuilding(s);
      setPointer(lineOverride);
    }
    let b = s.building;
    const line = b ? b.i : pointerRef.current;
    if (line === undefined || line === null || line < 0) {
      flash("That was the last line. For a repeated chorus, click its first line in the list to tap it again.", "warn");
      return;
    }
    const n = Math.max(1, wordsOf(parsed.lines[line]).length);
    if (!b) {
      // First word: this tap IS the line's start.
      const cue = { id: (cueSeq += 1), i: line, t, w: [0], wt: true, src: "tapw" };
      s.ids.add(cue.id);
      b = { id: cue.id, i: line, t, w: [0], n };
      s.building = b;
      s.stack.push({ kind: "line", id: cue.id, i: line });
      setCues((prev) => sortCues([...prev, cue]));
    } else {
      // Later words: offsets from the line start, always moving forward.
      const prevW = b.w;
      const off = round2(Math.max(prevW[prevW.length - 1] + 0.05, t - b.t));
      b = { ...b, w: [...prevW, off] };
      s.building = b;
      s.stack.push({ kind: "word", id: b.id, i: b.i, t: b.t, prevW, n });
      const w = b.w;
      setCues((prev) => prev.map((c) => (c.id === b.id ? { ...c, w, e: undefined } : c)));
    }
    if (b.w.length >= n) {
      // Line finished: give it an end just after its last word, so a slow
      // line is never cut off by the reading-speed estimate.
      const e = round2(b.t + b.w[b.w.length - 1] + 0.9);
      const id = b.id;
      setCues((prev) => prev.map((c) => (c.id === id ? { ...c, e } : c)));
      s.building = null;
      setPointer(nextSungLine(parsed, line));
      setWordPos(0);
    } else {
      setWordPos(b.w.length);
    }
    setDirty(true);
  };

  const tap = useCallback(
    (lineOverride) => {
      const a = audioRef.current;
      const s = sessionRef.current;
      if (!a || !s) return;
      if (tapModeRef.current === "word") {
        tapWord(a, s, lineOverride);
        return;
      }
      const line = lineOverride ?? pointerRef.current;
      if (line === undefined || line === null || line < 0) {
        flash("That was the last line. For a repeated chorus, click its first line in the list to tap it again.", "warn");
        return;
      }
      const t = Math.max(0, a.currentTime - delay * a.playbackRate);
      const cue = { id: (cueSeq += 1), i: line, t: round2(t), src: "tap" };
      s.ids.add(cue.id);
      s.stack.push(cue);
      setCues((prev) => sortCues([...prev, cue]));
      setPointer(nextSungLine(parsed, line));
      setDirty(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay, parsed]
  );

  const undoTap = useCallback(() => {
    const s = sessionRef.current;
    if (!s || !s.stack.length) return;
    const last = s.stack.pop();
    if (last.kind === "word") {
      // Take back one word: the line is in progress again from that word.
      const w = last.prevW;
      s.building = { id: last.id, i: last.i, t: last.t, w, n: last.n };
      setCues((prev) => prev.map((c) => (c.id === last.id ? { ...c, w, e: undefined } : c)));
      setPointer(last.i);
      setWordPos(w.length);
      return;
    }
    if (last.kind === "line") s.building = null;
    s.ids.delete(last.id);
    setCues((prev) => prev.filter((c) => c.id !== last.id));
    setPointer(last.i);
    setWordPos(0);
  }, []);

  const startOver = () => {
    if (cues.length && !window.confirm("Clear every timing for this song and start tapping from the top?")) return;
    setCues([]);
    setDirty(true);
    seek(0);
    pinnedRef.current = true;
    setPointer(parsed.sung[0] ?? -1);
    startRecording();
  };

  /* ---------- keyboard ---------- */

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.code === "Space") {
        e.preventDefault();
        if (document.activeElement?.tagName === "BUTTON") document.activeElement.blur();
        if (sessionRef.current) tap();
        else togglePlay();
      } else if (e.code === "Backspace" && sessionRef.current) {
        e.preventDefault();
        undoTap();
      } else if (e.code === "Escape" && sessionRef.current) {
        stopRecording();
      } else if (e.code === "ArrowLeft") {
        e.preventDefault();
        seek((audioRef.current?.currentTime || 0) - 2);
      } else if (e.code === "ArrowRight") {
        e.preventDefault();
        seek((audioRef.current?.currentTime || 0) + 2);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tap, undoTap, stopRecording]);

  /* ---------- editing timings ---------- */

  const updateCue = (id, patch) => {
    setCues((prev) => sortCues(prev.map((c) => (c.id === id ? { ...c, ...patch } : c))));
    setDirty(true);
  };
  const nudge = (id, delta) => {
    const c = cues.find((x) => x.id === id);
    if (!c) return;
    // A nudge throws away auto-sync's word timings and end: they were
    // measured from the old start and would now be wrong.
    //
    // Word-TAPPED lines (wt) are different: you tapped every word, so a nudge
    // moves the whole line — every word and the end — by the same amount.
    if (c.wt) {
      const t = round2(Math.max(0, c.t + delta));
      updateCue(id, { t, e: Number.isFinite(c.e) ? round2(c.e + (t - c.t)) : c.e, low: false });
      return;
    }
    updateCue(id, { t: round2(Math.max(0, c.t + delta)), w: undefined, e: undefined, low: false });
  };
  const removeCue = (id) => {
    setCues((prev) => prev.filter((c) => c.id !== id));
    setDirty(true);
  };
  // ✕ under a lyric line: delete that one time, with an Undo in the notice.
  const deleteTime = (cue) => {
    removeCue(cue.id);
    setNotice({
      tone: "info",
      text: `Deleted the ${formatTime(cue.t)} time for “${parsed.lines[cue.i]}”. Press Save to keep it that way.`,
      action: {
        label: "Undo",
        run: () => {
          setCues((prev) => sortCues([...prev.filter((c) => c.id !== cue.id), cue]));
          setDirty(true);
          setNotice(null);
        },
      },
      at: Date.now(),
    });
  };
  const shiftAll = (delta) => {
    setCues((prev) =>
      prev.map((c) => ({
        ...c,
        t: round2(Math.max(0, c.t + delta)),
        e: Number.isFinite(c.e) ? round2(Math.max(0, c.e + delta)) : c.e,
      }))
    );
    setDirty(true);
  };
  // A line that comes after the end marker still plays when you press its
  // time, so you can check it — the fade only applies to lines before it.
  const hear = (t) => {
    seek(Math.max(0, t - 1.5));
    play({ free: endAtRef.current !== null && t >= endAtRef.current });
  };

  const onLineClick = (i) => {
    if (sessionRef.current) {
      tap(i);
    } else {
      pinnedRef.current = true;
      setPointer(i);
      setWordPos(0);
      flash(`Next tap will be: “${parsed.lines[i]}”`);
    }
  };

  const useNewLyrics = () => {
    const { cues: moved, dropped } = remapCues(lines, topic.lyrics, cues);
    const nextLines = toLyricLines(topic.lyrics);
    setLines(nextLines);
    setCues(withIds(sortCues(moved)));
    setPointer(parseLyrics(nextLines).sung[0] ?? -1);
    setDirty(true);
    flash(
      dropped
        ? `Switched to the new lyrics. ${moved.length} timings carried across; ${dropped} belonged to lines that changed — tap those again.`
        : `Switched to the new lyrics. All ${moved.length} timings carried across.`,
      dropped ? "warn" : "ok"
    );
  };

  /* ---------- save ---------- */

  const timedLines = useMemo(() => new Set(cues.map((c) => c.i)), [cues]);

  // The timing that is "on" right now (cues are kept sorted by time).
  const nowCue = useMemo(() => {
    if (!(time > 0)) return null;
    let found = null;
    for (const c of cues) {
      if (c.t <= time) found = c;
      else break;
    }
    return found;
  }, [cues, time]);
  const nowCueId = nowCue?.id ?? null;

  // Keep the line being sung in view in the lyrics list while it plays.
  useEffect(() => {
    if (!follow || !playing || nowCue === null) return;
    const box = scriptRef.current;
    const el = box?.querySelector(`[data-line="${nowCue.i}"]`);
    if (!box || !el) return;
    const b = box.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.top >= b.top + 24 && r.bottom <= b.bottom - b.height * 0.35) return;
    box.scrollTo({ top: box.scrollTop + (r.top - b.top) - b.height * 0.3, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowCueId, follow, playing]);
  const untimed = parsed.sung.filter((i) => !timedLines.has(i));
  const lowCount = cues.filter((c) => c.low).length;

  const save = async () => {
    if (recording) stopRecording();
    if (published && !cues.length) {
      flash("There are no timings yet, so there is nothing to show students.", "warn");
      return;
    }
    setSaving(true);
    try {
      const anyTap = cues.some((c) => c.src === "tap");
      const payload = {
        title: topic.title || slug,
        musicUrl: topic.musicUrl,
        lines,
        cues,
        theme: themeKey,
        logoOpacity,
        endAt: endAt ?? null,
        published,
        reviewed: true,
        source: anyTap ? "tap" : sync?.source || "tap",
        duration: Number.isFinite(duration) ? round2(duration) : null,
      };
      const saved = await saveLyricSync(slug, payload);
      setDirty(false);
      flash(
        published
          ? "Saved. Students now see this sing-along in the Music popup."
          : "Saved. Not shown to students yet — tick “Show this sing-along to students” when it’s ready.",
        "ok"
      );
      onSaved({ ...(sync || {}), ...saved, cues: saved.cues, id: slug });
    } catch (err) {
      flash(`Could not save: ${err?.message || err}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const savedSync = sync;

  /* ---------- render ---------- */

  const pointerText = pointer >= 0 ? parsed.lines[pointer] : "— end of lyrics —";
  const pointerWords = pointer >= 0 ? wordsOf(parsed.lines[pointer]) : [];
  // Total words to tap in the song (each written line once).
  const songWordCount = parsed.sung.reduce((n, i) => n + wordsOf(parsed.lines[i]).length, 0);
  // A line counts as word-timed when one of its cues has a start for every word.
  const wordTimed = (i) => {
    const n = wordsOf(parsed.lines[i]).length;
    return cues.some((c) => c.i === i && Array.isArray(c.w) && c.w.length === n && n > 0);
  };

  return (
    <div className="sa-studio">
      <div className="sa-studio-head">
        <button
          type="button"
          className="lb-btn lb-btn-ghost lb-btn-sm"
          onClick={() => {
            if (dirty && !window.confirm("You have unsaved timings. Leave without saving?")) return;
            onClose();
          }}
        >
          ← All songs
        </button>
        <div className="sa-studio-title">
          <h3>{topic.title || slug}</h3>
          <span className="sa-muted">
            {slug} · {fileNameOf(topic.musicUrl)}
          </span>
        </div>
        {dirty && <span className="sa-pill-inline tone-amber">Unsaved changes</span>}
      </div>

      {songChanged && (
        <div className="sa-banner tone-red">
          <strong>The song file has changed</strong> since these timings were made (they were
          made for <code>{fileNameOf(sync.musicUrl)}</code>). If it is a new recording, the
          timings will be off — re-record or re-run auto-sync. If it is the same recording
          uploaded again, just press Save and the timings will move to the new file.
        </div>
      )}

      {lyricsChanged && (
        <div className="sa-banner tone-amber">
          <strong>The lyrics in the Topic Library were edited</strong> after this song was
          synced. The studio is showing the lyrics the timings were made for.
          <div className="sa-banner-actions">
            <button type="button" className="lb-btn lb-btn-sm" onClick={useNewLyrics}>
              Use the new lyrics
            </button>
            <span className="sa-muted">Timings are carried across for every line that didn’t change.</span>
          </div>
        </div>
      )}

      {sync?.source === "auto" && !sync?.reviewed && (
        <div className="sa-banner tone-blue">
          <strong>Auto-synced — please check it.</strong> Press Play and watch the preview.
          {lowCount > 0 ? (
            <> Lines marked <span className="sa-pill-inline tone-amber">check</span> are the ones the computer was least sure about.</>
          ) : null}
          {(sync?.autoStats?.unmatched || []).length > 0 ? (
            <> {sync.autoStats.unmatched.length} line(s) could not be found in the song — tap those in.</>
          ) : null}{" "}
          When it looks right, press Save.
        </div>
      )}

      {notice && (
        <div className={`sa-notice tone-${notice.tone}`} key={notice.at}>
          {notice.text}
          {notice.action && (
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm sa-notice-action" onClick={notice.action.run}>
              {notice.action.label}
            </button>
          )}
          <button type="button" className="sa-x" onClick={() => setNotice(null)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}

      {audioError && <div className="sa-banner tone-red">{audioError}</div>}

      <audio
        ref={audioRef}
        src={songUrl}
        preload="auto"
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onPlay={() => {
          setPlaying(true);
          cancelAnimationFrame(rafRef.current);
          rafRef.current = requestAnimationFrame(tick);
        }}
        onPause={() => setPlaying(false)}
        onSeeked={() => setTime(audioRef.current?.currentTime || 0)}
        onEnded={() => {
          setPlaying(false);
          if (sessionRef.current) stopRecording();
        }}
        onError={() => setAudioError("This song file could not be loaded. Check the Song field in the Topic Library.")}
      />

      <div className="sa-studio-grid">
        {/* ---------- left: preview + transport ---------- */}
        <div className="sa-col">
          <LyricsDisplay
            parsed={parsed}
            cues={cues}
            time={time}
            themeKey={themeKey}
            title={topic.title}
            logoOpacity={logoOpacity}
            fade={endingRef.current && !recording ? fadeLevelAt(endAt, time) : 1}
            className="sa-preview"
          />

          <div className="sa-transport">
            <button type="button" className="lb-btn sa-play" onClick={togglePlay}>
              {playing ? "❚❚ Pause" : "▶ Play"}
            </button>
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => seek(time - 5)}>
              −5s
            </button>
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => seek(time + 5)}>
              +5s
            </button>
            <span className="sa-clock">
              {formatTime(time)} / {formatTime(duration, 0)}
            </span>
            <label className="sa-inline-field">
              Speed
              <select value={rate} onChange={(e) => setRate(Number(e.target.value))}>
                {RATE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}×
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="sa-seek-wrap">
            <input
              type="range"
              className="sa-seek"
              min={0}
              max={duration || 0}
              step={0.01}
              value={Math.min(time, duration || 0)}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Song position"
            />
            {endAt !== null && duration > 0 && endAt < duration && (
              <span
                className="sa-seek-end"
                style={{
                  left: `${(endAt / duration) * 100}%`,
                  width: `${(Math.min(FADE_SECONDS, duration - endAt) / duration) * 100}%`,
                }}
                title={`Sing-along ends at ${formatTime(endAt)} and fades out`}
              />
            )}
          </div>

          <div className={`sa-endbox ${endAt !== null ? "is-set" : ""}`}>
            {endAt === null ? (
              <>
                <button
                  type="button"
                  className="lb-btn lb-btn-soft lb-btn-sm"
                  onClick={() => {
                    setEndAt(time);
                    flash(`The sing-along now ends at ${formatTime(time)} and fades out over ${FADE_SECONDS} seconds. Press Save.`, "ok");
                  }}
                  disabled={!duration || time <= 0}
                >
                  ■ End sing-along here ({formatTime(time)})
                </button>
                <span className="sa-muted">
                  Optional. From that point the screen and song fade out over {FADE_SECONDS} seconds,
                  and the video stops when the fade ends. Without it, the song plays to the end.
                </span>
              </>
            ) : (
              <>
                <strong className="sa-endbox-label">■ Ends at</strong>
                <TimeInput value={endAt} onCommit={(t) => setEndAt(t)} />
                <span className="sa-muted">
                  fades out {FADE_SECONDS}s → stops at {formatTime(stopTimeOf(endAt))}.{" "}
                  {playPast
                    ? "Playing past the end — no fade while this is ticked."
                    : `▶ Play fades and stops here, just like the video. Play from past the end replays the last ${ENDING_LEAD_IN} seconds and the fade.`}
                </span>
                <div className="sa-endbox-actions">
                  <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={previewEnding} title="Play the last few seconds and the fade">
                    ▶ Preview ending
                  </button>
                  <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setEndAt(endAt - 0.1)} title="End a little sooner">
                    −0.1
                  </button>
                  <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setEndAt(endAt + 0.1)} title="End a little later">
                    +0.1
                  </button>
                  <button
                    type="button"
                    className="lb-btn lb-btn-ghost lb-btn-sm"
                    onClick={() => setEndAt(time)}
                    disabled={time <= 0}
                    title="Move the end to where the song is now"
                  >
                    Move to {formatTime(time)}
                  </button>
                  <button type="button" className="lb-btn lb-btn-danger lb-btn-sm" onClick={() => setEndAt(null)} title="Play the song to its end">
                    ✕ Remove
                  </button>
                  <label className="sa-check sa-playpast" title="Turn the fade off for now, to listen on and pick a later ending">
                    <input type="checkbox" checked={playPast} onChange={(e) => setPlayPast(e.target.checked)} />
                    Play past the end (to pick a later one)
                  </label>
                </div>
              </>
            )}
          </div>

          <div className={`sa-tapbox ${recording ? "is-recording" : ""}`}>
            <div className="sa-tapbox-head">
              <div className="sa-tapbox-title">
                <strong>Tap along</strong>
                <div className="sa-tapmode" role="group" aria-label="Tap mode">
                  <button
                    type="button"
                    className={tapMode === "line" ? "is-on" : ""}
                    aria-pressed={tapMode === "line"}
                    disabled={recording}
                    onClick={() => setTapMode("line")}
                    title="One tap at the start of each line"
                  >
                    By line
                  </button>
                  <button
                    type="button"
                    className={tapMode === "word" ? "is-on" : ""}
                    aria-pressed={tapMode === "word"}
                    disabled={recording}
                    onClick={() => setTapMode("word")}
                    title="One tap on every word — the colour change follows each word exactly"
                  >
                    By word
                  </button>
                </div>
              </div>
              <span className="sa-muted">
                {tapMode === "word"
                  ? recording
                    ? "Tap SPACE (or the big button) on EVERY word as it is sung. Backspace = undo one word, Esc = stop."
                    : `Press Record, then tap SPACE on every word (${songWordCount} words in this song). Lines stay the same — each word just gets its own start, so the colour lands right on it.`
                  : recording
                  ? "Tap SPACE (or the big button) the moment each line starts. Backspace = undo, Esc = stop."
                  : "Press Record, then tap SPACE as each line starts. Only the part you tap over is replaced."}
              </span>
            </div>
            <div className="sa-tapbox-row">
              {!recording ? (
                <>
                  <button type="button" className="lb-btn sa-rec" onClick={startRecording} disabled={!topic.musicUrl}>
                    ● Record from {formatTime(time, 0)}
                  </button>
                  <button type="button" className="lb-btn lb-btn-ghost" onClick={startOver} disabled={!topic.musicUrl}>
                    ⟲ Start over from the top
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="sa-tap-btn"
                    // pointerdown, not click: a click fires on release, which
                    // would make every touch-screen tap land late.
                    onPointerDown={(e) => {
                      e.preventDefault();
                      tap();
                    }}
                  >
                    {tapMode === "word" ? (
                      <>
                        TAP WORD
                        {pointer >= 0 ? (
                          <>
                            <span className="sa-tap-words">
                              {pointerWords.map((w, k) => (
                                <b
                                  key={k}
                                  className={k < wordPos ? "is-done" : k === wordPos ? "is-cur" : ""}
                                >
                                  {w}
                                </b>
                              ))}
                            </span>
                            <span className="sa-tap-count">
                              word {Math.min(wordPos + 1, pointerWords.length)} of {pointerWords.length}
                            </span>
                          </>
                        ) : (
                          <span>{pointerText}</span>
                        )}
                      </>
                    ) : (
                      <>
                        TAP
                        <span>{pointerText}</span>
                      </>
                    )}
                  </button>
                  <div className="sa-tap-side">
                    <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={undoTap}>
                      {tapMode === "word" ? "↶ Undo word" : "↶ Undo tap"}
                    </button>
                    <button type="button" className="lb-btn lb-btn-danger lb-btn-sm" onClick={stopRecording}>
                      ■ Stop
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="sa-tapbox-foot">
              <label className="sa-inline-field">
                Tap-delay fix
                <select value={delay} onChange={(e) => setDelay(Number(e.target.value))}>
                  {DELAY_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d === 0 ? "off" : `${d}s`}
                    </option>
                  ))}
                </select>
              </label>
              <span className="sa-muted">
                Everyone taps a little late. This moves each tap earlier by that much. If lines
                still light up late, raise it.
              </span>
            </div>
          </div>
        </div>

        {/* ---------- right: the lyrics script ---------- */}
        <div className="sa-col">
          <div className="sa-script-head">
            <div className="sa-script-head-row">
              <strong>Lyrics</strong>
              <label className="sa-check sa-follow">
                <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
                Follow the song
              </label>
              <button
                type="button"
                className="lb-btn lb-btn-save lb-btn-sm"
                onClick={save}
                disabled={saving || (!dirty && !songChanged && sync?.reviewed !== false)}
                title="Save the timings"
              >
                {saving ? "Saving…" : dirty ? "Save timings" : "Saved"}
              </button>
            </div>
            <span className="sa-muted">
              {parsed.sung.length - untimed.length} of {parsed.sung.length} lines timed.{" "}
              {recording
                ? "Click a line to tap it right now (use this for a repeated chorus)."
                : "Watch the preview: if a line lights up early press +0.1, late press −0.1. Click a time to hear it. Click a line to make it the next tap."}
            </span>
            <span className="sa-muted sa-script-legend">
              Each time under a line is one moment it starts (a line sung twice has two).{" "}
              <b>✕</b> deletes that one time.{" "}
              <span className="sa-time-chip is-low sa-legend-chip">amber</span> = auto-sync wasn’t sure;
              press <b className="sa-legend-ok">✓</b> when it sounds right and the amber goes away.
            </span>
          </div>
          <div className="sa-script" ref={scriptRef}>
            {parsed.sections.map((section, sIdx) => (
              <div key={sIdx} className="sa-script-section">
                {section.label && <div className="sa-script-label">{section.label}</div>}
                {section.lineIdx.map((i) => {
                  const lineCues = cues.filter((c) => c.i === i);
                  const isNext = i === pointer;
                  const isNow = nowCue?.i === i;
                  return (
                    <div
                      key={i}
                      data-line={i}
                      className={`sa-script-line ${isNext ? "is-next" : ""} ${isNow ? "is-now" : ""} ${lineCues.length ? "" : "is-untimed"}`}
                      onClick={() => onLineClick(i)}
                      role="button"
                      tabIndex={-1}
                    >
                      <span className="sa-script-marker">{isNext ? "▶" : ""}</span>
                      <div className="sa-script-body">
                        <span className="sa-script-text">
                          {parsed.lines[i]}
                          {lineCues.length > 0 && wordTimed(i) && (
                            <span className="sa-pill-inline tone-green sa-word-pill" title="Every word in this line has its own start">
                              word-timed
                            </span>
                          )}
                        </span>
                        <span className="sa-script-times">
                          {lineCues.length === 0 ? (
                            <em>not timed</em>
                          ) : (
                            lineCues.map((c, n) => (
                              <span
                                key={c.id}
                                className={`sa-time-group ${c.id === nowCueId ? "is-now" : ""} ${c.low ? "is-low" : ""}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {lineCues.length > 1 && <span className="sa-time-n">{n + 1}</span>}
                                <button
                                  type="button"
                                  className="sa-nudge"
                                  title="Light up earlier (−0.1s)"
                                  aria-label="Earlier"
                                  onClick={() => nudge(c.id, -0.1)}
                                >
                                  −
                                </button>
                                <button
                                  type="button"
                                  className={`sa-time-chip ${c.low ? "is-low" : ""}`}
                                  title={c.low ? "Auto-sync wasn’t sure about this one. Click to hear it." : "Play from just before"}
                                  onClick={() => hear(c.t)}
                                >
                                  {formatTime(c.t)}
                                </button>
                                <button
                                  type="button"
                                  className="sa-nudge"
                                  title="Light up later (+0.1s)"
                                  aria-label="Later"
                                  onClick={() => nudge(c.id, 0.1)}
                                >
                                  +
                                </button>
                                {c.low && (
                                  <button
                                    type="button"
                                    className="sa-nudge sa-nudge-ok"
                                    title="Sounds right — clears the amber “check” mark"
                                    onClick={() => updateCue(c.id, { low: false })}
                                  >
                                    ✓
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="sa-nudge sa-nudge-del"
                                  title="Delete this time"
                                  aria-label={`Delete the ${formatTime(c.t)} time`}
                                  onClick={() => deleteTime(c)}
                                >
                                  ✕
                                </button>
                              </span>
                            ))
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            {parsed.credits.length > 0 && (
              <div className="sa-script-credit">
                {parsed.credits.join(" · ")} — not sung. The screen always shows “© Smart Board
                Learning. All rights reserved.” along the bottom.
              </div>
            )}
          </div>

          <LookPicker
            themeKey={themeKey}
            logoOpacity={logoOpacity}
            onTheme={(k) => {
              setThemeKey(k);
              setDirty(true);
            }}
            onLogoOpacity={(v) => {
              setLogoOpacity(v);
              setDirty(true);
            }}
          />
        </div>
      </div>

      {/* ---------- timing list ---------- */}
      <div className="sa-timings">
        <div className="sa-timings-head">
          <button type="button" className="sa-help-toggle" onClick={() => setShowTimings((v) => !v)}>
            <span>{showTimings ? "▾" : "▸"}</span> Timing list ({cues.length})
          </button>
          {showTimings && cues.length > 0 && (
            <div className="sa-timings-tools">
              <span className="sa-muted">Whole song a bit early/late?</span>
              <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => shiftAll(-0.1)}>
                All −0.1s
              </button>
              <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => shiftAll(0.1)}>
                All +0.1s
              </button>
            </div>
          )}
        </div>
        {showTimings && (
          <>
            <p className="sa-muted sa-timings-hint">
              The same −/+ buttons are on each line in the Lyrics list above. Use this list to type
              an exact time or delete a timing. Each row is one moment a line starts. A chorus sung
              three times has three rows.
              ▶ plays from just before that moment. −0.1 makes the line light up earlier, +0.1
              later. You can also type a time like <code>1:05.3</code>.
            </p>
            {cues.length === 0 ? (
              <p className="sa-empty-row">No timings yet. Press ● Record and tap along, or run auto-sync.</p>
            ) : (
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Starts at</th>
                    <th>Line</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {cues.map((c, idx) => {
                    const page = parsed.pages[parsed.pageOfLine[c.i]];
                    const next = cues[idx + 1];
                    const isNow = time >= c.t && (!next || time < next.t);
                    return (
                      <tr key={c.id} className={`${c.low ? "is-low" : ""} ${isNow && playing ? "is-now" : ""}`}>
                        <td className="sa-num">{idx + 1}</td>
                        <td>
                          <TimeInput
                            value={c.t}
                            onCommit={(t) =>
                              updateCue(
                                c.id,
                                c.wt
                                  ? { t, e: Number.isFinite(c.e) ? round2(c.e + (t - c.t)) : c.e }
                                  : { t, w: undefined, e: undefined }
                              )
                            }
                          />
                        </td>
                        <td>
                          {page?.label && <span className="sa-row-label">{page.label}</span>}
                          {parsed.lines[c.i]}
                          {c.low && <span className="sa-pill-inline tone-amber">check</span>}
                        </td>
                        <td className="sa-row-actions">
                          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => hear(c.t)} title="Play from just before">
                            ▶
                          </button>
                          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => nudge(c.id, -0.1)} title="Earlier">
                            −0.1
                          </button>
                          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => nudge(c.id, 0.1)} title="Later">
                            +0.1
                          </button>
                          {c.low && (
                            <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" onClick={() => updateCue(c.id, { low: false })} title="It sounds right">
                              ✓ OK
                            </button>
                          )}
                          <button type="button" className="lb-btn lb-btn-danger lb-btn-sm" onClick={() => removeCue(c.id)} title="Delete this timing">
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* ---------- save bar ---------- */}
      <div className="sa-savebar">
        <label className="sa-check">
          <input
            type="checkbox"
            checked={published}
            onChange={(e) => {
              setPublished(e.target.checked);
              setDirty(true);
            }}
          />
          Show this sing-along to students
        </label>
        <span className="sa-muted sa-savebar-note">
          {untimed.length > 0 && cues.length > 0
            ? `${untimed.length} line${untimed.length === 1 ? " is" : "s are"} not timed — they’ll show but never light up.`
            : ""}
        </span>
        <button
          type="button"
          className="lb-btn lb-btn-save"
          onClick={save}
          // An unreviewed auto-sync can be saved as-is: saving IS the review.
          disabled={saving || (!dirty && !songChanged && sync?.reviewed !== false)}
        >
          {saving ? "Saving…" : sync && sync.reviewed === false && !dirty ? "Save as checked" : "Save"}
        </button>
      </div>

      <ExportBox
        topic={topic}
        slug={slug}
        sync={savedSync}
        parsed={parsed}
        cues={cues}
        themeKey={themeKey}
        logoOpacity={logoOpacity}
        endAt={endAt}
        songUrl={songUrl}
        dirty={dirty}
        onSaved={onSaved}
        onAttached={onAttached}
      />

      <FadedSongBox
        topic={topic}
        slug={slug}
        sync={savedSync}
        endAt={endAt}
        songUrl={songUrl}
        dirty={dirty}
        onSaved={onSaved}
        onSongAttached={onSongAttached}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Look: background colour + logo strength                             */
/* ------------------------------------------------------------------ */

const Swatch = ({ k, on, onPick }) => {
  const th = THEMES[k];
  return (
    <button
      type="button"
      className={`sa-swatch ${on ? "is-on" : ""}`}
      onClick={() => onPick(k)}
      title={th.label}
      aria-pressed={on}
    >
      <span
        className="sa-swatch-chip"
        style={{ background: `linear-gradient(135deg, ${th.bg[0]}, ${th.bg[1]})`, color: th.ink }}
      >
        Aa <b style={{ color: th.lit }}>Aa</b>
      </span>
      <span className="sa-swatch-name">{th.label}</span>
    </button>
  );
};

const LookPicker = ({ themeKey, logoOpacity, onTheme, onLogoOpacity }) => {
  const deepKeys = THEME_ORDER.filter((k) => THEMES[k].family === "deep");
  const softKeys = THEME_ORDER.filter((k) => THEMES[k].family === "soft");
  const pct = Math.round(logoOpacity * 100);
  return (
    <div className="sa-look">
      <div className="sa-look-head">
        <strong>🎨 Look</strong>
        <span className="sa-muted">
          Pick this song’s background. The preview, the student screen and the video all use it.
          “Aa” shows the words, and the second “Aa” is the colour they light up.
        </span>
      </div>
      <div className="sa-swatch-group-label">Rich colours · white words (best on a smart board)</div>
      <div className="sa-swatches">
        {deepKeys.map((k) => (
          <Swatch key={k} k={k} on={themeKey === k} onPick={onTheme} />
        ))}
      </div>
      <div className="sa-swatch-group-label">Soft colours · dark words (gentlest up close)</div>
      <div className="sa-swatches">
        {softKeys.map((k) => (
          <Swatch key={k} k={k} on={themeKey === k} onPick={onTheme} />
        ))}
      </div>
      <label className="sa-logo-row">
        Logo behind the words
        <input
          type="range"
          min={0}
          max={MAX_LOGO_OPACITY * 100}
          step={1}
          value={pct}
          onChange={(e) => onLogoOpacity(Number(e.target.value) / 100)}
        />
        <span className="sa-logo-value">{pct === 0 ? "off" : `${pct}% strength`}</span>
      </label>
      <p className="sa-muted" style={{ margin: "6px 0 0" }}>
        Around 15% keeps the words easy to read. Above about 30%, the logo starts competing with the
        lyrics.
      </p>
    </div>
  );
};

/** A time box that only commits on blur / Enter, so typing "1:0" mid-way does not jump. */
const TimeInput = ({ value, onCommit }) => {
  const [text, setText] = useState(formatTime(value, 2));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    setText(formatTime(value, 2));
    setBad(false);
  }, [value]);
  const commit = () => {
    const t = parseTime(text);
    if (t === null) {
      setBad(true);
      return;
    }
    setBad(false);
    if (Math.abs(t - value) > 0.004) onCommit(round2(t));
    else setText(formatTime(value, 2));
  };
  return (
    <input
      className={`sa-time-input ${bad ? "is-bad" : ""}`}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      aria-label="Start time"
    />
  );
};

/* ------------------------------------------------------------------ */
/* MP4 export                                                          */
/* ------------------------------------------------------------------ */

const SIZES = {
  "720": { width: 1280, height: 720, label: "720p (faster upload)" },
  "1080": { width: 1920, height: 1080, label: "1080p (sharper)" },
};

const CorsHelp = () => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <div className="sa-banner tone-red">
      <strong>The browser wasn’t allowed to download this song for recording.</strong> Your
      storage bucket’s CORS setting has to list the site you’re on now:{" "}
      <code>{origin}</code>
      <br />
      Open your existing SBL <code>cors.json</code>, add <code>"{origin}"</code> to its{" "}
      <code>"origin"</code> list (change nothing else — the upload settings in that file must
      stay), then apply it:
      <pre className="sa-pre">
        gcloud storage buckets update gs://sbleducationalportal.firebasestorage.app --cors-file=cors.json
      </pre>
      Applying a CORS file REPLACES the bucket’s whole setting, so always apply your full SBL file,
      never a smaller one. Reload this page and try again.
    </div>
  );
};

const ExportBox = ({
  topic,
  slug,
  sync,
  parsed,
  cues,
  themeKey,
  logoOpacity,
  endAt,
  songUrl,
  dirty,
  onSaved,
  onAttached,
}) => {
  const [attaching, setAttaching] = useState(false);
  const attach = async (url) => {
    setAttaching(true);
    try {
      // The topic's real library id, not the tool key.
      await attachSingAlongVideo(topic.id || slug, url);
      onAttached(url);
    } catch (err) {
      setError({ code: "attach", message: `Could not update the topic: ${err?.message || err}` });
    } finally {
      setAttaching(false);
    }
  };
  const onTopic = topic.singAlongUrl || "";
  const attachedHere = Boolean(onTopic && sync?.videoUrl && mediaKey(onTopic) === mediaKey(sync.videoUrl));
  const [size, setSize] = useState("720");
  const [audible, setAudible] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | done | uploading | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // { blob, url, mime, ext, codecLabel }
  const [error, setError] = useState(null); // { code, message }
  // "Checking this browser can play MP4…" — shown under the progress bar.
  const [status, setStatus] = useState("");
  const abortRef = useRef(null);

  useEffect(
    () => () => {
      if (result?.url) URL.revokeObjectURL(result.url);
      abortRef.current?.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const supported = canExportVideo();
  const mime = pickVideoMime();
  const willBeMp4 = Boolean(mime && mime.startsWith("video/mp4"));
  const video = videoStateOf(sync);

  const make = async () => {
    setError(null);
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);
    setProgress(0);
    setPhase("recording");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const out = await exportSingAlongVideo({
        musicUrl: songUrl || topic.musicUrl,
        parsed,
        cues,
        themeKey,
        title: topic.title,
        logoOpacity,
        width: SIZES[size].width,
        height: SIZES[size].height,
        audible,
        endAt,
        signal: controller.signal,
        onProgress: (f) => setProgress(f),
        onStatus: setStatus,
      });
      setResult({ ...out, url: URL.createObjectURL(out.blob) });
      setPhase("done");
    } catch (err) {
      if (err?.code === "aborted") {
        setPhase("idle");
      } else {
        setError({ code: err?.code || "error", message: err?.message || String(err) });
        setPhase("error");
      }
    } finally {
      abortRef.current = null;
      setStatus("");
    }
  };

  const upload = async () => {
    if (!result) return;
    setPhase("uploading");
    setProgress(0);
    try {
      // <slug><year>singalong.mp4 — the same pattern as <slug>2026video.mp4
      // and <slug>2026song.mp3, with its own word, so a sing-along can never
      // land on (and overwrite) a topic's regular video. Re-making the video
      // for the same topic replaces only its own sing-along file.
      //
      // Flat in videos/ on purpose: the storage rule for videos/ only matches
      // files directly in that folder, and mediaIndex already indexes it.
      // match-media-to-topics.mjs and seed-topic-library.mjs skip "singalong"
      // files so they are never mistaken for a topic's video.
      const path = `videos/${singAlongFileName(slug, result.ext)}`;
      const task = uploadBytesResumable(storageRef(storage, path), result.blob, {
        contentType: result.mime,
      });
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => setProgress(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
          reject,
          resolve
        );
      });
      const url = await getDownloadURL(task.snapshot.ref);
      const sig = syncSignature({
        lines: parsed.lines,
        cues,
        theme: themeKey,
        title: topic.title || slug,
        logoOpacity,
        endAt,
      });
      const saved = await saveLyricSync(slug, {
        videoUrl: url,
        videoPath: path,
        videoMime: result.mime,
        videoSig: sig,
        videoMadeAt: new Date().toISOString(),
      });
      onSaved({ ...(sync || {}), ...saved, id: slug });
      // Put it on the topic's 🎤 Sing-along button straight away.
      // The topic's real library id, not the tool key.
      await attachSingAlongVideo(topic.id || slug, url);
      onAttached(url);
      setPhase("saved");
    } catch (err) {
      setError({ code: "upload", message: err?.message || String(err) });
      setPhase("error");
    }
  };

  return (
    <div className="sa-export">
      <div className="sa-export-head">
        <strong>🎬 Make a video</strong>
        <span className="sa-muted">
          Records the sing-along exactly as the preview shows it, with the song, as a{" "}
          {willBeMp4 ? "MP4" : "WebM (this browser can’t make MP4 — use current Chrome or Edge for MP4)"} file.
          It records in real time: a 3-minute song takes 3 minutes. Keep this tab open and on screen
          until it finishes.
          {endAt !== null
            ? ` It stops at ${formatTime(stopTimeOf(endAt))}, after the ${FADE_SECONDS}-second fade-out.`
            : " It runs to the end of the song (set “End sing-along here” to finish sooner)."}
        </span>
      </div>

      {sync?.videoUrl && (
        <div className="sa-export-current">
          Saved video:{" "}
          <a href={sync.videoUrl} target="_blank" rel="noreferrer">
            {sync.videoPath || "open"} ↗
          </a>{" "}
          <span className={`sa-pill-inline tone-${video.key === "ok" ? "green" : "amber"}`}>
            {video.key === "ok" ? "up to date" : "out of date — timings changed since"}
          </span>
          <button
            type="button"
            className="lb-btn lb-btn-ghost lb-btn-sm"
            onClick={() => navigator.clipboard?.writeText(sync.videoUrl)}
          >
            Copy link
          </button>
        </div>
      )}

      <div className="sa-export-current">
        🎤 Sing-along button on this topic:{" "}
        {attachedHere ? (
          <>
            <span className="sa-pill-inline tone-green">on — plays this video</span>
            <button
              type="button"
              className="lb-btn lb-btn-ghost lb-btn-sm"
              disabled={attaching}
              onClick={() => attach("")}
            >
              Remove button
            </button>
          </>
        ) : onTopic ? (
          <>
            <span className="sa-pill-inline tone-blue">on — plays {mediaKey(onTopic).split("/").pop()}</span>
            {sync?.videoUrl && (
              <button
                type="button"
                className="lb-btn lb-btn-soft lb-btn-sm"
                disabled={attaching}
                onClick={() => attach(sync.videoUrl)}
              >
                Use this studio’s video instead
              </button>
            )}
          </>
        ) : (
          <>
            <span className="sa-pill-inline tone-grey">off</span>
            {sync?.videoUrl ? (
              <button
                type="button"
                className="lb-btn lb-btn-soft lb-btn-sm"
                disabled={attaching}
                onClick={() => attach(sync.videoUrl)}
              >
                Add the button
              </button>
            ) : (
              <span className="sa-muted">Make a video and save it to SBL, and the button is added for you.</span>
            )}
          </>
        )}
        {topic.status !== "published" && (onTopic || sync?.videoUrl) && (
          <span className="sa-muted">
            {" "}This topic is still a draft in the Topic Library, so portal pages won’t show its button until it’s published.
          </span>
        )}
      </div>

      {!supported ? (
        <div className="sa-banner tone-amber">
          This browser can’t record video. Open the Layout Builder in the latest Chrome or Edge on a computer.
        </div>
      ) : (
        <>
          <div className="sa-export-row">
            <label className="sa-inline-field">
              Size
              <select value={size} onChange={(e) => setSize(e.target.value)} disabled={phase === "recording"}>
                {Object.entries(SIZES).map(([k, s]) => (
                  <option key={k} value={k}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-check">
              <input type="checkbox" checked={audible} onChange={(e) => setAudible(e.target.checked)} disabled={phase === "recording"} />
              Play the song out loud while recording
            </label>
            {phase === "recording" ? (
              <button type="button" className="lb-btn lb-btn-danger" onClick={() => abortRef.current?.abort()}>
                ■ Cancel
              </button>
            ) : (
              <button
                type="button"
                className="lb-btn"
                onClick={make}
                disabled={dirty || !cues.length || phase === "uploading"}
                title={dirty ? "Save your timings first" : ""}
              >
                🎬 Make video
              </button>
            )}
          </div>
          {dirty && <p className="sa-muted">Save your timings first — the video is made from the saved version.</p>}
          {!cues.length && <p className="sa-muted">Sync the song first — there are no timings to record.</p>}

          {(phase === "recording" || phase === "uploading") && (
            <div className="sa-progress">
              <div className="sa-progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
              <span>
                {phase === "recording" ? "Recording" : "Uploading"} {Math.round(progress * 100)}%
                {phase === "recording" ? " — keep this tab visible" : ""}
              </span>
            </div>
          )}

          {status && phase === "recording" && <p className="sa-muted">{status}</p>}

          {error && (error.code === "cors" ? <CorsHelp /> : <div className="sa-banner tone-red">{error.message}</div>)}

          {result && (phase === "done" || phase === "uploading" || phase === "saved") && (
            <div className="sa-export-result">
              <ExportPreview url={result.url} label={result.codecLabel} />
              {result.logoMissing && (
                <div className="sa-banner tone-amber" style={{ marginTop: 10 }}>
                  This video was made <strong>without the logo</strong>: the browser wasn’t allowed
                  to load the logo file for recording. That happens when the storage CORS setting
                  doesn’t list <code>{window.location.origin}</code>. Add that address to the{" "}
                  <code>"origin"</code> list in your SBL <code>cors.json</code>, apply it, and
                  make the video again.
                </div>
              )}
              <div className="sa-export-actions">
                <a className="lb-btn lb-btn-soft" href={result.url} download={`${slug}-singalong.${result.ext}`}>
                  ⬇ Download {result.ext.toUpperCase()}
                </a>
                <button type="button" className="lb-btn" onClick={upload} disabled={phase === "uploading" || phase === "saved"}>
                  {phase === "saved" ? "✓ Saved to SBL" : "☁ Save to SBL storage"}
                </button>
              </div>
              {phase === "saved" && (
                <p className="sa-muted">
                  Saved as <code>videos/{singAlongFileName(slug, result.ext)}</code> and added to this topic’s{" "}
                  <strong>🎤 Sing-along</strong> button on the educator and student portals. It plays
                  like the Video button and gives its own star.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Faded song (MP3)                                                    */
/* ------------------------------------------------------------------ */
//
// For a sing-along with an end marker: an MP3 of the song that fades out at
// the same spot and ends with it, saved to music/{topic}{year}song-faded.mp3.
// On the topic's Song field, the Music button and the sing-along video then
// finish together.
//
// Writes: that one file, the faded* fields on lyricSync/{slug}, and — only
// when you press the button, or when the topic already uses the faded file
// and you re-make it — topicLibrary/{id}.musicUrl. The original song file is
// never touched or deleted.

const FadedSongBox = ({ topic, slug, sync, endAt, songUrl, dirty, onSaved, onSongAttached }) => {
  const [phase, setPhase] = useState("idle"); // idle | making | done | uploading | saved | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // { blob, url, stopAt }
  const [error, setError] = useState(null); // { code, message }
  const [busy, setBusy] = useState(false);
  const abortRef = useRef(null);

  useEffect(
    () => () => {
      if (result?.url) URL.revokeObjectURL(result.url);
      abortRef.current?.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const hasFaded = Boolean(sync?.fadedMusicUrl);
  const onFaded = isFadedSong(sync, topic.musicUrl);
  const fadedEnd = Number(sync?.fadedEndAt);
  const fadedCurrent = hasFaded && endAt !== null && Math.abs(fadedEnd - endAt) < 0.01;

  // Nothing to show for a sing-along with no end that never had a faded song.
  if (endAt === null && !hasFaded && !onFaded) return null;

  const make = async () => {
    setError(null);
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);
    setProgress(0);
    setPhase("making");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const out = await makeFadedSong({
        musicUrl: songUrl,
        endAt,
        signal: controller.signal,
        onProgress: setProgress,
      });
      setResult({ ...out, url: URL.createObjectURL(out.blob) });
      setPhase("done");
    } catch (err) {
      if (err?.code === "aborted") setPhase("idle");
      else {
        setError({ code: err?.code || "error", message: err?.message || String(err) });
        setPhase("error");
      }
    } finally {
      abortRef.current = null;
    }
  };

  const setTopicSong = async (url, label) => {
    setBusy(true);
    setError(null);
    try {
      // The topic's real library id, not the tool key.
      await attachTopicSong(topic.id || slug, url);
      onSongAttached(url);
    } catch (err) {
      setError({ code: "attach", message: `Could not change ${label}: ${err?.message || err}` });
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    if (!result) return;
    setPhase("uploading");
    setProgress(0);
    try {
      const path = `music/${fadedSongFileName(slug)}`;
      const task = uploadBytesResumable(storageRef(storage, path), result.blob, {
        contentType: "audio/mpeg",
      });
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => setProgress(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
          reject,
          resolve
        );
      });
      const url = await getDownloadURL(task.snapshot.ref);
      const saved = await saveLyricSync(slug, {
        fadedMusicUrl: url,
        fadedMusicPath: path,
        fadedMusicKey: mediaKey(url) || path,
        fadedEndAt: endAt,
        fadedSourceUrl: songUrl,
        fadedSourceKey: mediaKey(songUrl),
        fadedMadeAt: new Date().toISOString(),
      });
      onSaved({ ...(sync || {}), ...saved, id: slug });
      // Already on the faded song? Point the topic at the new file so it
      // never plays the old fade (or a stale download link).
      if (onFaded) await setTopicSong(url, "the topic's song");
      setPhase("saved");
    } catch (err) {
      setError({ code: "upload", message: err?.message || String(err) });
      setPhase("error");
    }
  };

  return (
    <div className="sa-export sa-faded">
      <div className="sa-export-head">
        <strong>🎵 Faded song (MP3)</strong>
        <span className="sa-muted">
          Makes an MP3 of the song that fades out at the same spot as this sing-along
          {endAt !== null ? ` (${formatTime(endAt)}, ending at ${formatTime(stopTimeOf(endAt))})` : ""} and
          ends with it, so the Music button and the sing-along finish together. It takes a few
          seconds, not the length of the song. Saved to <code>music/{fadedSongFileName(slug)}</code>.
          The original song file is kept.
        </span>
      </div>

      {hasFaded && (
        <div className="sa-export-current">
          Saved faded song:{" "}
          <a href={sync.fadedMusicUrl} target="_blank" rel="noreferrer">
            {sync.fadedMusicPath || "open"} ↗
          </a>{" "}
          {fadedCurrent ? (
            <span className="sa-pill-inline tone-green">up to date</span>
          ) : (
            <span className="sa-pill-inline tone-amber">
              made for an end at {Number.isFinite(fadedEnd) ? formatTime(fadedEnd) : "?"} — make it again
            </span>
          )}
        </div>
      )}

      <div className="sa-export-current">
        This topic’s Song:{" "}
        {onFaded ? (
          <>
            <span className="sa-pill-inline tone-green">the faded MP3</span>
            {sync?.fadedSourceUrl && (
              <button
                type="button"
                className="lb-btn lb-btn-ghost lb-btn-sm"
                disabled={busy}
                onClick={() => setTopicSong(sync.fadedSourceUrl, "the topic's song")}
              >
                Switch back to the original
              </button>
            )}
          </>
        ) : (
          <>
            <span className="sa-pill-inline tone-grey">the original ({fileNameOf(topic.musicUrl)})</span>
            {hasFaded && fadedCurrent && (
              <button
                type="button"
                className="lb-btn lb-btn-soft lb-btn-sm"
                disabled={busy}
                onClick={() => setTopicSong(sync.fadedMusicUrl, "the topic's song")}
              >
                Use the faded song for this topic
              </button>
            )}
          </>
        )}
        {topic.status !== "published" && (
          <span className="sa-muted"> This topic is still a draft in the Topic Library.</span>
        )}
      </div>

      {onFaded && endAt === null && (
        <div className="sa-banner tone-amber">
          The end marker was removed, but this topic still plays the faded MP3, so its Music button
          still fades out early. Switch back to the original, or set the end again.
        </div>
      )}
      {onFaded && endAt !== null && hasFaded && !fadedCurrent && (
        <div className="sa-banner tone-amber">
          The end moved since the faded song was made. Make it again and save it. That replaces the
          file and updates this topic’s Song for you.
        </div>
      )}

      <p className="sa-muted" style={{ margin: "4px 0 8px" }}>
        You can also attach it by hand: Topic Library → this topic’s <strong>Song</strong> →{" "}
        <strong>Find existing</strong> → it’s the file ending in <code>song-faded.mp3</code>.
        The sing-along stays live on either song file.
      </p>

      {!canMakeFadedSong() ? (
        <div className="sa-banner tone-amber">This browser can’t make audio files. Use the latest Chrome or Edge.</div>
      ) : (
        <>
          <div className="sa-export-row">
            {phase === "making" ? (
              <button type="button" className="lb-btn lb-btn-danger" onClick={() => abortRef.current?.abort()}>
                ■ Cancel
              </button>
            ) : (
              <button
                type="button"
                className="lb-btn"
                onClick={make}
                disabled={dirty || endAt === null || phase === "uploading"}
                title={dirty ? "Save first" : endAt === null ? "Set “End sing-along here” first" : ""}
              >
                🎵 Make faded song
              </button>
            )}
            {dirty && <span className="sa-muted">Save first — it is made from the saved end point.</span>}
            {!dirty && endAt === null && <span className="sa-muted">Set “End sing-along here” first.</span>}
          </div>

          {(phase === "making" || phase === "uploading") && (
            <div className="sa-progress">
              <div className="sa-progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
              <span>
                {phase === "making" ? "Making MP3" : "Uploading"} {Math.round(progress * 100)}%
              </span>
            </div>
          )}

          {error && (error.code === "cors" ? <CorsHelp /> : <div className="sa-banner tone-red">{error.message}</div>)}

          {result && ["done", "uploading", "saved"].includes(phase) && (
            <div className="sa-export-result">
              <audio src={result.url} controls className="sa-faded-audio" />
              <p className="sa-muted" style={{ margin: "6px 0 0" }}>
                {formatTime(result.stopAt, 1)} long. Listen to the end to check the fade.
              </p>
              <div className="sa-export-actions">
                <a className="lb-btn lb-btn-soft" href={result.url} download={fadedSongFileName(slug)}>
                  ⬇ Download MP3
                </a>
                <button type="button" className="lb-btn" onClick={upload} disabled={phase === "uploading" || phase === "saved"}>
                  {phase === "saved" ? "✓ Saved to SBL music" : "☁ Save to SBL music"}
                </button>
                {phase === "saved" && !onFaded && sync?.fadedMusicUrl && (
                  <button
                    type="button"
                    className="lb-btn lb-btn-soft"
                    disabled={busy}
                    onClick={() => setTopicSong(sync.fadedMusicUrl, "the topic's song")}
                  >
                    Use it as this topic’s Song
                  </button>
                )}
              </div>
              {phase === "saved" && (
                <p className="sa-muted">
                  Saved as <code>music/{fadedSongFileName(slug)}</code>.{" "}
                  {onFaded
                    ? "This topic’s Song now plays the new faded file."
                    : "Press “Use it as this topic’s Song”, or attach it later from the Topic Library with Find existing."}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Go live in bulk                                                     */
/* ------------------------------------------------------------------ */
//
// "Go live + make video" for several songs at once, from the song list.
// For each ticked song, in order:
//
//   1. lyricSync/{slug}: published + reviewed (exactly what ticking "Show
//      this sing-along to students" and pressing Save does). Timings, look
//      and end marker are left as they are.
//   2. (optional) records the video from those saved timings, the same way
//      🎬 Make video does: real time, tab must stay visible.
//   3. saves it to videos/{slug}{year}singalong.mp4 and puts it on the
//      topic's 🎤 Sing-along button (topicLibrary/{id}.singAlongUrl).
//
// Songs whose song file changed since syncing ("Song changed") can't be
// ticked: their timings are for a different recording.

// Which rows can be ticked: an auto-sync to check, a saved-but-not-live one,
// or a live one whose video is missing or out of date.
export const canGoLiveInBulk = (r) =>
  ["auto", "ready"].includes(r.status.key) || (r.status.key === "live" && r.video.key !== "ok");

const songSecondsOf = (sync) => {
  const stop = stopTimeOf(endAtOf(sync));
  const d = Number(sync?.duration);
  if (stop !== null) return Number.isFinite(d) && d > 0 ? Math.min(stop, d) : stop;
  return Number.isFinite(d) && d > 0 ? d : 180; // unknown: guess 3 minutes
};

const minutesText = (sec) => {
  const m = Math.max(1, Math.round(sec / 60));
  if (m < 60) return `about ${m} minute${m === 1 ? "" : "s"}`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `about ${h} hour${h === 1 ? "" : "s"}${rest ? ` ${rest} min` : ""}`;
};

/** The lyric lines this sing-along was timed to (what the studio would open). */
const syncLinesOf = (topic, sync) =>
  sync?.lines?.length ? toLyricLines(sync.lines) : toLyricLines(topic.lyrics);

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

const SingAlongPanel = () => {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(() => store.get("sbl.singalong.help", true));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topics, setTopics] = useState([]);
  const [syncs, setSyncs] = useState({});
  const [subject, setSubject] = useState("science");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeSlug, setActiveSlug] = useState(null);
  const panelRef = useRef(null);

  // Go live in bulk
  const [selected, setSelected] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [withVideo, setWithVideo] = useState(() => store.get("sbl.singalong.bulkVideo", true));
  const [bulkSize, setBulkSize] = useState("720");
  // { index, total, title, step: "publish"|"record"|"upload", progress, status }
  const [run, setRun] = useState(null);
  const [runResult, setRunResult] = useState(null); // { done: [], noVideo: [], failed: [{ title, why }], stopped }
  const abortRef = useRef(null);
  const stopAfterRef = useRef(false);
  const [stopAfter, setStopAfter] = useState(false);

  useEffect(() => store.set("sbl.singalong.bulkVideo", withVideo), [withVideo]);

  // Leaving the page mid-run would cut a recording off.
  useEffect(() => {
    if (!run) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [run]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, s] = await Promise.all([getAllLibraryTopics(), getAllLyricSyncs()]);
      setTopics(t);
      setSyncs(s);
    } catch (err) {
      setError(
        err?.code === "permission-denied"
          ? "Permission denied reading lyricSync. Deploy the updated firestore.rules (see SINGALONG-SETUP.md)."
          : `Could not load: ${err?.message || err}`
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !topics.length && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    store.set("sbl.singalong.help", helpOpen);
  }, [helpOpen]);

  // Which key each topic's sing-along lives under. See src/utils/topicKey.js.
  const toolKey = useMemo(() => makeToolKey(topics), [topics]);

  const rows = useMemo(
    () =>
      topics.map((topic) => {
        // Own sing-along only. A twin with none yet starts empty here and
        // gets its own document on Save (students keep seeing the shared
        // one meanwhile, if the song file is the same).
        const slug = toolKey(topic);
        const sync =
          syncs[slug] ||
          (slug === legacyKey(topic.slug || topic.id) ? syncs[exactKey(topic.slug || topic.id)] : null) ||
          null;
        return {
          topic: { ...topic, id: topic.id || topic.slug, slug },
          sync,
          status: statusOf(topic, sync),
          video: videoStateOf(sync),
          sungCount: parseLyrics(topic.lyrics).sung.length,
          lyricsEdited: Boolean(sync?.lines && linesDiffer(sync.lines, topic.lyrics)),
        };
      }),
    [topics, syncs, toolKey]
  );

  const counts = useMemo(() => {
    const out = {};
    SUBJECTS.forEach((s) => {
      const inSubject = rows.filter((r) => (r.topic.subject || "other") === s);
      out[s] = {
        total: inSubject.length,
        live: inSubject.filter((r) => r.status.key === "live").length,
      };
    });
    return out;
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => (r.topic.subject || "other") === subject)
      .filter((r) => matchesFilter(r.status, filter))
      .filter(
        (r) =>
          !q ||
          String(r.topic.title || "").toLowerCase().includes(q) ||
          r.topic.slug.includes(q)
      );
  }, [rows, subject, filter, search]);

  const active = activeSlug ? rows.find((r) => r.topic.slug === activeSlug) : null;

  const totals = useMemo(
    () => ({
      live: rows.filter((r) => r.status.key === "live").length,
      check: rows.filter((r) => ["auto", "stale"].includes(r.status.key)).length,
      withSong: rows.filter((r) => !["nosong", "nolyrics"].includes(r.status.key)).length,
    }),
    [rows]
  );

  const openStudio = (slug) => {
    setActiveSlug(slug);
    setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  /* ---------- Go live in bulk ---------- */

  const videoSupported = canExportVideo();
  const videoMime = pickVideoMime();
  const makesMp4 = Boolean(videoMime && videoMime.startsWith("video/mp4"));
  const videoOn = withVideo && videoSupported;

  const picked = rows.filter((r) => selected.has(r.topic.slug) && canGoLiveInBulk(r));
  const visibleTickable = visible.filter(canGoLiveInBulk);
  const allVisibleTicked = visibleTickable.length > 0 && visibleTickable.every((r) => selected.has(r.topic.slug));
  const visibleAuto = visible.filter((r) => r.status.key === "auto");
  const needsVideo = (r) => r.video.key !== "ok";
  const recordSeconds = picked.filter(needsVideo).reduce((n, r) => n + songSecondsOf(r.sync), 0);

  const toggle = (slug) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  const tickRows = (list, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      list.forEach((r) => (on ? next.add(r.topic.slug) : next.delete(r.topic.slug)));
      return next;
    });

  const patchSync = (slug, saved) =>
    setSyncs((prev) => ({ ...prev, [slug]: { ...(prev[slug] || {}), ...saved, id: slug } }));

  /** Records, uploads and attaches the video for one song. Returns the URL. */
  const makeVideoFor = async (topic, sync, lines, index, total) => {
    const slug = topic.slug;
    const title = topic.title || slug;
    const parsed = parseLyrics(lines);
    const cues = cleanCues(sync.cues, parsed);
    const { width, height } = SIZES[bulkSize];

    const record = async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        return await exportSingAlongVideo({
          musicUrl: fullSongUrlOf(sync, topic.musicUrl),
          parsed,
          cues,
          themeKey: themeKeyOf(sync.theme),
          title,
          logoOpacity: logoOpacityOf(sync),
          width,
          height,
          audible: false,
          endAt: endAtOf(sync),
          signal: controller.signal,
          onProgress: (f) => setRun((r) => (r ? { ...r, step: "record", progress: f } : r)),
          onStatus: (s) => setRun((r) => (r ? { ...r, status: s } : r)),
        });
      } finally {
        abortRef.current = null;
      }
    };

    setRun({ index, total, title, step: "record", progress: 0, status: "" });
    let out;
    try {
      out = await record();
    } catch (err) {
      // The exporter strikes off a format that recorded but won't play and
      // asks for another go; do that once by itself.
      if (err?.code !== "unplayable") throw err;
      setRun((r) => (r ? { ...r, progress: 0, status: "Trying again in another format…" } : r));
      out = await record();
    }

    setRun((r) => (r ? { ...r, step: "upload", progress: 0, status: "" } : r));
    const path = `videos/${singAlongFileName(slug, out.ext)}`;
    const task = uploadBytesResumable(storageRef(storage, path), out.blob, { contentType: out.mime });
    await new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snap) =>
          setRun((r) => (r ? { ...r, progress: snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0 } : r)),
        reject,
        resolve
      );
    });
    const url = await getDownloadURL(task.snapshot.ref);
    // Same fingerprint the list uses (videoStateOf) so it reads "Video ✓".
    const videoSig = syncSignature({
      lines,
      cues: sync.cues,
      theme: sync.theme,
      title,
      logoOpacity: sync.logoOpacity,
      endAt: sync.endAt,
    });
    const saved = await saveLyricSync(slug, {
      videoUrl: url,
      videoPath: path,
      videoMime: out.mime,
      videoSig,
      videoMadeAt: new Date().toISOString(),
    });
    patchSync(slug, saved);
    await attachSingAlongVideo(topic.id || slug, url);
    setTopics((prev) => prev.map((t) => ((t.id || t.slug) === topic.id ? { ...t, singAlongUrl: url } : t)));
    return url;
  };

  const goLive = async () => {
    const list = picked;
    if (!list.length) return;
    setConfirming(false);
    setRunResult(null);
    stopAfterRef.current = false;
    setStopAfter(false);
    const done = [];
    const doneSlugs = [];
    const noVideo = [];
    const failed = [];
    let stopped = false;

    for (let i = 0; i < list.length; i += 1) {
      if (stopAfterRef.current) {
        stopped = true;
        break;
      }
      const r = list[i];
      const topic = r.topic;
      const sync = r.sync;
      const slug = topic.slug;
      const title = topic.title || slug;
      const lines = syncLinesOf(topic, sync);
      setRun({ index: i, total: list.length, title, step: "publish", progress: 0, status: "" });

      // 1. Live for students (same fields the studio's Save writes for this).
      try {
        const payload = { title, published: true, reviewed: true };
        if (!sync?.lines?.length) payload.lines = lines;
        const saved = await saveLyricSync(slug, payload);
        patchSync(slug, saved);
      } catch (err) {
        failed.push({ title, why: `Could not make it live: ${err?.message || err}` });
        continue;
      }

      // 2 + 3. Video → cloud → 🎤 Sing-along button.
      if (!videoOn || !needsVideo(r)) {
        done.push(title);
        doneSlugs.push(slug);
        continue;
      }
      try {
        await makeVideoFor(topic, { ...sync, title, lines }, lines, i, list.length);
        done.push(title);
        doneSlugs.push(slug);
      } catch (err) {
        if (err?.code === "aborted") {
          noVideo.push({ title, why: "recording stopped" });
          stopped = true;
          break;
        }
        const why =
          err?.code === "cors"
            ? `the browser wasn’t allowed to download the song (storage CORS doesn’t list ${window.location.origin})`
            : err?.message || String(err);
        noVideo.push({ title, why });
        // A CORS problem fails every song the same way: stop rather than
        // spend the next hour failing.
        if (err?.code === "cors" || err?.code === "unsupported") {
          stopped = true;
          break;
        }
      }
    }

    setSelected((prev) => {
      const next = new Set(prev);
      doneSlugs.forEach((s) => next.delete(s));
      return next;
    });
    setRun(null);
    setRunResult({ done, noVideo, failed, stopped });
  };

  return (
    <section className="lb-card sa-panel" ref={panelRef}>
      <div className="lb-card-head">
        <span className="lb-step sa-step">♪</span>
        <div>
          <h2>Sing-along studio</h2>
          <p className="lb-hint">
            Turn a topic’s song and lyrics into a sing-along: the lyrics show a section at a time
            and light up as they’re sung. Publish it to the Music button, or make an MP4.
          </p>
        </div>
        <div className="sa-head-actions">
          {open && !active && (
            <button type="button" className="lb-btn lb-btn-soft" onClick={load} disabled={loading || Boolean(run)}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
          <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)} disabled={Boolean(run)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && (
        <>
          {error && <div className="sa-banner tone-red">{error}</div>}

          {active ? (
            <Studio
              key={active.topic.slug}
              topic={active.topic}
              sync={active.sync}
              onClose={() => setActiveSlug(null)}
              onSaved={(saved) => setSyncs((prev) => ({ ...prev, [active.topic.slug]: { ...(prev[active.topic.slug] || {}), ...saved } }))}
              onAttached={(url) =>
                setTopics((prev) =>
                  prev.map((t) =>
                    (t.id || t.slug) === active.topic.id ? { ...t, singAlongUrl: url } : t
                  )
                )
              }
              onSongAttached={(url) =>
                setTopics((prev) =>
                  prev.map((t) =>
                    (t.id || t.slug) === active.topic.id ? { ...t, musicUrl: url } : t
                  )
                )
              }
            />
          ) : (
            <>
              <HowItWorks open={helpOpen} onToggle={() => setHelpOpen((v) => !v)} />

              <div className="sa-summary">
                <span>
                  <strong>{totals.live}</strong> live
                </span>
                <span>
                  <strong>{totals.check}</strong> need a check
                </span>
                <span>
                  <strong>{totals.withSong}</strong> songs with lyrics
                </span>
              </div>

              {/* ---------- Go live in bulk ---------- */}

              {runResult && (
                <div
                  className={`sa-banner sa-bulk-result tone-${
                    runResult.failed.length || runResult.noVideo.length ? "amber" : "green"
                  }`}
                >
                  {runResult.done.length > 0 && (
                    <div>
                      <strong>
                        {runResult.done.length} sing-along{runResult.done.length === 1 ? " is" : "s are"} live
                      </strong>
                      {videoOn ? " with the video saved and the 🎤 Sing-along button on" : ""}: {runResult.done.join(", ")}.
                    </div>
                  )}
                  {runResult.noVideo.length > 0 && (
                    <div>
                      <strong>Live, but no video yet ({runResult.noVideo.length}):</strong>
                      <ul>
                        {runResult.noVideo.map((f) => (
                          <li key={f.title}>
                            {f.title} — {f.why}
                          </li>
                        ))}
                      </ul>
                      They’re still ticked, so you can press the button again to make just their videos.
                    </div>
                  )}
                  {runResult.failed.length > 0 && (
                    <div>
                      <strong>Not changed ({runResult.failed.length}):</strong>
                      <ul>
                        {runResult.failed.map((f) => (
                          <li key={f.title}>
                            {f.title} — {f.why}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {runResult.stopped && <div>Stopped before the end of the list; the rest are still ticked.</div>}
                  <button type="button" className="sa-banner-x" onClick={() => setRunResult(null)} aria-label="Close">
                    ✕
                  </button>
                </div>
              )}

              {run ? (
                <div className="sa-bulk-run">
                  <div className="sa-bulk-run-head">
                    <strong>
                      Song {run.index + 1} of {run.total}: {run.title}
                    </strong>
                    <span className="sa-muted">
                      {run.step === "publish"
                        ? "Making it live…"
                        : run.step === "record"
                        ? "Recording the video — keep this tab open and on screen"
                        : "Saving the video to SBL storage…"}
                    </span>
                  </div>
                  <div className="sa-progress">
                    <div
                      className="sa-progress-bar"
                      style={{ width: `${Math.round(((run.index + (run.step === "publish" ? 0 : run.progress)) / run.total) * 100)}%` }}
                    />
                    <span>
                      {Math.round(((run.index + (run.step === "publish" ? 0 : run.progress)) / run.total) * 100)}% of all
                      {run.step !== "publish" ? ` · this song ${Math.round(run.progress * 100)}%` : ""}
                    </span>
                  </div>
                  {run.status && <p className="sa-muted">{run.status}</p>}
                  <div className="sa-bulk-run-actions">
                    <button
                      type="button"
                      className="lb-btn lb-btn-ghost lb-btn-sm"
                      disabled={stopAfter}
                      onClick={() => {
                        stopAfterRef.current = true;
                        setStopAfter(true);
                      }}
                    >
                      {stopAfter ? "Will stop after this song" : "Stop after this song"}
                    </button>
                    <button
                      type="button"
                      className="lb-btn lb-btn-danger lb-btn-sm"
                      onClick={() => {
                        stopAfterRef.current = true;
                        setStopAfter(true);
                        abortRef.current?.abort();
                      }}
                    >
                      ■ Stop now
                    </button>
                  </div>
                </div>
              ) : (
                <div className="sa-bulkbar">
                  <button
                    type="button"
                    className="lb-btn lb-btn-ghost lb-btn-sm"
                    onClick={() => tickRows(visibleAuto, true)}
                    disabled={!visibleAuto.length}
                  >
                    Select all “Auto — check it” shown ({visibleAuto.length})
                  </button>
                  {picked.length > 0 && (
                    <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setSelected(new Set())}>
                      Clear
                    </button>
                  )}
                  <span className="sa-muted">
                    <strong>{picked.length}</strong> selected
                  </span>
                  <button
                    type="button"
                    className="lb-btn lb-btn-save"
                    disabled={!picked.length || confirming}
                    onClick={() => {
                      setRunResult(null);
                      setConfirming(true);
                    }}
                  >
                    🚀 Go live{videoOn ? " + make videos" : ""} ({picked.length})
                  </button>
                </div>
              )}

              {confirming && !run && (
                <div className="sa-bulk-confirm">
                  <div className="sa-bulk-confirm-head">
                    <strong>
                      Make {picked.length} sing-along{picked.length === 1 ? "" : "s"} live for students?
                    </strong>
                    <span className="sa-muted">
                      Each one is saved as checked and shown in its topic’s Music popup, using the timings,
                      look and ending it has now.
                    </span>
                  </div>

                  <div className="sa-bulk-opts">
                    <label className="sa-check">
                      <input
                        type="checkbox"
                        checked={videoOn}
                        disabled={!videoSupported}
                        onChange={(e) => setWithVideo(e.target.checked)}
                      />
                      Also make each video, save it to SBL storage and add the 🎤 Sing-along button
                    </label>
                    {videoOn && (
                      <label className="sa-inline-field">
                        Size
                        <select value={bulkSize} onChange={(e) => setBulkSize(e.target.value)}>
                          {Object.entries(SIZES).map(([k, s]) => (
                            <option key={k} value={k}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>

                  {!videoSupported && (
                    <div className="sa-banner tone-amber">
                      This browser can’t record video, so only the go-live part will run. Use the latest Chrome or
                      Edge on a computer to make the videos.
                    </div>
                  )}
                  {videoOn && (
                    <div className="sa-banner tone-blue">
                      Videos record in real time, one after another:{" "}
                      <strong>{minutesText(recordSeconds)}</strong> for{" "}
                      {picked.filter(needsVideo).length} video{picked.filter(needsVideo).length === 1 ? "" : "s"}. Keep this
                      tab open and on screen the whole time (a hidden tab freezes the video). Each saves as{" "}
                      <code>videos/…{new Date().getFullYear()}singalong.{makesMp4 ? "mp4" : "webm"}</code>
                      {makesMp4 ? "" : " — this browser makes WebM; use current Chrome or Edge for MP4"}. Songs that already
                      have an up-to-date video keep it.
                    </div>
                  )}

                  <div className="sa-table-wrap">
                    <table className="sa-table">
                      <thead>
                        <tr>
                          <th>Topic</th>
                          <th>Now</th>
                          <th>Will</th>
                        </tr>
                      </thead>
                      <tbody>
                        {picked.map((r) => (
                          <tr key={r.topic.slug}>
                            <td>
                              <div className="sa-topic-title">{r.topic.title || r.topic.slug}</div>
                              <div className="sa-muted sa-topic-slug">
                                {r.topic.slug}
                                {r.topic.status !== "published" ? " · draft topic (button shows once it’s published)" : ""}
                              </div>
                            </td>
                            <td>
                              <span className={`sa-pill-inline tone-${STATUS_TONES[r.status.key]}`}>{r.status.label}</span>
                              {r.lyricsEdited && <span className="sa-pill-inline tone-amber">lyrics edited</span>}
                              {(r.sync?.autoStats?.unmatched || []).length > 0 && (
                                <span className="sa-pill-inline tone-amber">
                                  {r.sync.autoStats.unmatched.length} line(s) not timed
                                </span>
                              )}
                            </td>
                            <td>
                              {r.status.key === "live" ? "Stay live" : "Go live"}
                              {videoOn && needsVideo(r)
                                ? ` + ${r.video.key === "old" ? "re-make" : "make"} video (${formatTime(songSecondsOf(r.sync), 0)})`
                                : videoOn
                                ? " · keeps its video"
                                : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="sa-bulk-confirm-actions">
                    <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setConfirming(false)}>
                      Cancel
                    </button>
                    <button type="button" className="lb-btn lb-btn-save" onClick={goLive}>
                      Yes — go live{videoOn ? " and make videos" : ""} ({picked.length})
                    </button>
                  </div>
                </div>
              )}

              <div className="sa-controls-row">
                <div className="sa-tabs">
                  {SUBJECTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`sa-tab ${subject === s ? "is-on" : ""}`}
                      onClick={() => setSubject(s)}
                    >
                      {SUBJECT_LABELS[s]}
                      <span className="sa-tab-count">
                        {counts[s]?.live ?? 0}/{counts[s]?.total ?? 0}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="sa-tabs">
                  {STATUS_FILTERS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={`sa-tab sa-tab-sm ${filter === f.key ? "is-on" : ""}`}
                      onClick={() => setFilter(f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <input
                  className="sa-search"
                  placeholder="Search songs…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {loading && !rows.length ? (
                <p className="sa-muted">Loading songs…</p>
              ) : visible.length === 0 ? (
                <p className="sa-empty-row">No songs match. Try another subject or filter.</p>
              ) : (
                <div className="sa-table-wrap">
                  <table className="sa-table sa-list">
                    <thead>
                      <tr>
                        <th className="sa-check-col">
                          <input
                            type="checkbox"
                            aria-label="Tick every song shown that can go live"
                            title="Tick every song shown that can go live"
                            checked={allVisibleTicked}
                            disabled={!visibleTickable.length || Boolean(run)}
                            onChange={(e) => tickRows(visibleTickable, e.target.checked)}
                          />
                        </th>
                        <th>Topic</th>
                        <th>Lines</th>
                        <th>Sing-along</th>
                        <th>Video</th>
                        <th aria-label="Open" />
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((r) => {
                        const canOpen = !["nosong", "nolyrics"].includes(r.status.key);
                        return (
                          <tr
                            key={r.topic.slug}
                            className={`${canOpen ? "" : "is-disabled"} ${
                              selected.has(r.topic.slug) && canGoLiveInBulk(r) ? "is-picked" : ""
                            }`}
                          >
                            <td className="sa-check-col">
                              {canGoLiveInBulk(r) && (
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${r.topic.title || r.topic.slug}`}
                                  title="Tick to go live (and make the video) with the others"
                                  checked={selected.has(r.topic.slug)}
                                  disabled={Boolean(run)}
                                  onChange={() => toggle(r.topic.slug)}
                                />
                              )}
                            </td>
                            <td>
                              <div className="sa-topic-title">{r.topic.title || r.topic.slug}</div>
                              <div className="sa-muted sa-topic-slug">
                                {r.topic.slug}
                                {r.topic.status !== "published" ? " · draft topic" : ""}
                              </div>
                            </td>
                            <td>{r.sungCount || "—"}</td>
                            <td>
                              <span className={`sa-pill-inline tone-${STATUS_TONES[r.status.key]}`}>{r.status.label}</span>
                              {r.lyricsEdited && <span className="sa-pill-inline tone-amber">lyrics edited</span>}
                            </td>
                            <td>
                              <span className={`sa-video-${r.video.key}`}>{r.video.label}</span>
                              {r.topic.singAlongUrl && (
                                <div className="sa-muted" title="This topic shows a 🎤 Sing-along button on the portal">
                                  🎤 button on
                                </div>
                              )}
                            </td>
                            <td className="sa-row-actions">
                              <button
                                type="button"
                                className={`lb-btn lb-btn-sm ${r.status.key === "todo" ? "" : "lb-btn-soft"}`}
                                disabled={!canOpen || Boolean(run)}
                                onClick={() => openStudio(r.topic.slug)}
                                title={canOpen ? "" : "Add a song file and lyrics in the Topic Library first"}
                              >
                                {r.status.key === "todo" ? "Sync" : "Open"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default SingAlongPanel;
